import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, EmptyState, PrimaryButton } from "../components/common";
import { toast } from "sonner";
import { Trash, ArrowCounterClockwise, FileXls } from "@phosphor-icons/react";

const TYPE_LABELS = {
  contrato: "Contrato", estimacion: "Estimación", tarea: "Tarea", foto: "Foto",
  contratista: "Contratista", documento: "Documento", empleado: "Empleado",
  partida: "Partida", concurso: "Concurso", cotizacion: "Cotización", gasto: "Gasto",
  bitacora: "Bitácora", hito: "Hito", auditoria: "Auditoría",
};

export default function DeletedRecords() {
  const { user } = useAuth();
  const canRestore = ["admin", "gerente_general"].includes(user?.role) || !!user?.can_delete;
  const [items, setItems] = useState([]);

  const load = () => api.get("/deleted-records").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const restore = async (id) => {
    if (!window.confirm("¿Restaurar este elemento?")) return;
    try { await api.post(`/deleted-records/${id}/restore`); toast.success("Elemento restaurado"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader overline="Auditoría" title="Eliminados"
        action={items.length > 0 && <PrimaryButton onClick={() => window.open(`${api.defaults.baseURL}/deleted-records/excel`, "_blank")} data-testid="deleted-excel-btn"><FileXls size={18} weight="fill" /> Exportar a Excel</PrimaryButton>} />
      {items.length === 0 ? (
        <EmptyState icon={Trash} title="Sin eliminaciones" subtitle="Aquí queda registro de todo lo que se borra: qué era, quién lo borró y a qué hora." />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left">{["Fecha y hora", "Tipo", "Detalle de lo eliminado", "Eliminado por", "Rol", ""].map((h) => <th key={h} className="px-4 py-3 overline">{h}</th>)}</tr></thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} data-testid={`deleted-row-${r.id}`} className="border-b border-border last:border-0 hover:bg-muted/50">
                  <td className="px-4 py-3 whitespace-nowrap">{new Date(r.deleted_at).toLocaleString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-sm font-semibold bg-red-50 text-red-700 text-xs">{TYPE_LABELS[r.entity_type] || r.entity_type}</span></td>
                  <td className="px-4 py-3">{r.summary}</td>
                  <td className="px-4 py-3 font-semibold">{r.deleted_by}</td>
                  <td className="px-4 py-3 capitalize text-muted-foreground">{r.deleted_by_role}</td>
                  <td className="px-4 py-3 text-right">
                    {r.restored_at ? <span className="text-xs font-semibold text-success">Restaurado</span>
                      : canRestore && <button onClick={() => restore(r.id)} data-testid={`restore-${r.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"><ArrowCounterClockwise size={14} /> Restaurar</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
